import { DefaultHeaders, RequestMetaData } from "./client";
import { ValueOf } from "./utility";
import { EndedActions, WaitingActions } from "./liveChat";
import { ZendeskSidebarTicket } from "./wiseOwlZendesk";
import { WiseOwlUser } from "./user";

/**
 * SHARED
 */
export interface WiseOwlApiResponse<Type> {
  data: Type;
  message: string;
  statusCode: number;
  success: boolean;
}

export enum WiseOwlIngress {
  console = "console",
  osc = "osc",
  oscUngated = "oscUngated",
  zendesk = "zendesk",
  salesforce = "salesforce",
  sendGrid = "sendGrid",
  wiseOwlApp = "WISEOWL_APP",
}

export const WiseOwlApplicationEnum = {
  messagingInsights: "messagingInsights",
  messagingLogs: "consoleMessagingLogs",
  messagingLogsDetails: "consoleMessagingLogsDetails",
};
export type WiseOwlApplicationEnumType = ValueOf<typeof WiseOwlApplicationEnum>;

export type WiseOwlGetAccessTokenHeaders = WiseOwlGetAccessTokenHeadersZendesk;

export interface WiseOwlGetAccessTokenHeadersZendesk extends DefaultHeaders {
  "zendesk-user-id": string;
}

export type WiseOwlHeaders = WiseOwlHeadersOscConsole | WiseOwlHeadersZendesk;

export interface WiseOwlHeadersOscConsole extends DefaultHeaders {
  "x-twilio-csrf": string;
}

export interface WiseOwlHeadersZendesk extends DefaultHeaders {
  "wozd-auth": string;
}

export interface WiseOwlHeadersSalesforce extends DefaultHeaders {
  "salesforce-auth": string;
}

export interface WiseOwlHeadersWiseOwlApp extends DefaultHeaders {
  "x-twilio-e2-ingress": WiseOwlIngress.wiseOwlApp;
  "x-twilio-e2-auth-token": string;
  "x-twilio-e2-session-id": string;
}
export interface WiseOwlHeadersSendgrid extends DefaultHeaders {
  "i-sendgrid-auth-mako-token": string;
  "i-sendgrid-auth-user-id": string;
}

export interface WiseOwlCreateUpdateBodyDefaults {
  input: string;
  stream: boolean;
}

export interface WiseOwlCreateUpdateBodyOsc
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlCreateUpdateBodyOscUngated
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId?: never;
  stargateUngatedUserId: string;
  ticket?: never;
}

export interface WiseOwlCreateUpdateBodyZendesk
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket: ZendeskSidebarTicket;
}

export interface WiseOwlCreateUpdateBodyConsole
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId: WiseOwlApplicationEnumType;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlCreateUpdateBodySalesforce
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlCreateUpdateBodyWiseOwlApp
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlCreateUpdateBodySendGrid
  extends WiseOwlCreateUpdateBodyDefaults {
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlCreateFeedbackBodyOsc {
  applicationId?: never;
  feedback: string;
  positive: boolean;
  stargateUngatedUserId?: never;
}

export interface WiseOwlCreateFeedbackBodyOscUngated {
  applicationId?: never;
  feedback: string;
  positive: boolean;
  stargateUngatedUserId: string;
}

export interface WiseOwlCreateFeedbackBodyConsole {
  applicationId: string;
  feedback: string;
  positive: boolean;
  stargateUngatedUserId?: never;
}

export interface WiseOwlCreateFeedbackBodyInternal {
  applicationId?: never;
  feedback: string;
  positive: boolean;
  stargateUngatedUserId?: never;
}

export interface WiseOwlCreateFeedbackBodySendGrid {
  applicationId?: never;
  feedback: string;
  positive: boolean;
}

export enum MessageCompletionStatus {
  InProgress = "inProgress",
  Complete = "complete",
  Error = "error",
}

export interface WiseOwlSyncDocumentContent {
  blocks: WiseOwlBlock[];
  status: MessageCompletionStatus;
}

/**
 * GET ACCESS TOKEN
 */
export type WiseOwlGetAccessTokeByIngressArgs =
  WiseOwlGetAccessTokeByIngressArgsZendesk;

export interface WiseOwlGetAccessTokeByIngressArgsZendesk
  extends RequestMetaData {
  headers: WiseOwlGetAccessTokenHeadersZendesk;
  ingress: WiseOwlIngress;
}

export interface WiseOwlGetAccessTokenResponseData {
  token: string;
}

/**
 * GET HOME
 */
export type WiseOwlGetHomeArgs =
  | WiseOwlGetHomeArgsOsc
  | WiseOwlGetHomeArgsUngatedOsc
  | WiseOwlGetHomeArgsConsole
  | WiseOwlGetHomeArgsZendesk
  | WiseOwlGetHomeArgsWiseOwlApp
  | WiseOwlGetHomeArgsSalesforce
  | WiseOwlGetHomeArgsSendgrid;

export interface WiseOwlGetHomeArgsOsc extends RequestMetaData {
  applicationId?: never;
  ingress: WiseOwlIngress.osc;
  stargateUngatedUserId?: never;
}

export interface WiseOwlGetHomeArgsUngatedOsc extends RequestMetaData {
  applicationId?: never;
  ingress: WiseOwlIngress.oscUngated;
  stargateUngatedUserId: string;
}

export interface WiseOwlGetHomeArgsConsole extends RequestMetaData {
  applicationId: string;
  ingress: WiseOwlIngress.console;
  stargateUngatedUserId?: never;
}

export interface WiseOwlGetHomeArgsZendesk extends RequestMetaData {
  applicationId?: never;
  headers: WiseOwlHeadersZendesk;
  ingress: WiseOwlIngress.zendesk;
  stargateUngatedUserId?: never;
}

export interface WiseOwlGetHomeArgsSalesforce extends RequestMetaData {
  applicationId?: never;
  headers: WiseOwlHeadersSalesforce;
  ingress: WiseOwlIngress.salesforce;
  stargateUngatedUserId?: never;
}

export interface WiseOwlGetHomeArgsSendgrid extends RequestMetaData {
  applicationId?: never;
  ingress: WiseOwlIngress.sendGrid;
  headers: WiseOwlHeadersSendgrid;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlGetHomeArgsWiseOwlApp extends RequestMetaData {
  applicationId?: never;
  headers: WiseOwlHeadersWiseOwlApp;
  // TODO update with WiseOwlApp ingress blocks
  ingress: WiseOwlIngress.wiseOwlApp;
  stargateUngatedUserId?: never;
}

export interface WiseOwlGetHomeResponseData {
  blocks: PromptSuggestionGroupBlock[] | IntelligentDiscoveryHomeBlock[];
}

/**
 * GET CHATS BY USER
 */

export interface WiseOwlGetChatsByUserArgs extends RequestMetaData {
  headers: WiseOwlHeadersOscConsole;
}

/**
 * GET CHAT BY ID
 */

// Note: OSC ungated GET chat by ID is not currently supported
export type WiseOwlGetChatByIdArgs =
  | WiseOwlGetChatByIdArgsOsc
  | WiseOwlGetChatByIdArgsConsole
  | WiseOwlGetChatByIdArgsZendesk
  | WiseOwlGetChatByIdArgsWiseOwlApp
  | WiseOwlGetChatByIdArgsSalesforce
  | WiseOwlGetChatByIdArgsSendGrid;

export interface WiseOwlGetChatByIdArgsSendGrid
  extends WiseOwlGetChatByIdArgsBase {
  applicationId?: never;
  interruptedChatId?: string;
  headers: WiseOwlHeadersSendgrid;
  ingress: WiseOwlIngress.sendGrid;
  ticketId?: never;
}

export interface WiseOwlGetChatByIdArgsBase extends RequestMetaData {
  chatId: string;
}

export interface WiseOwlGetChatByIdArgsOsc extends WiseOwlGetChatByIdArgsBase {
  applicationId?: never;
  interruptedChatId?: string;
  headers?: never;
  ingress: WiseOwlIngress.osc;
  ticketId?: never;
}

export interface WiseOwlGetChatByIdArgsConsole
  extends WiseOwlGetChatByIdArgsBase {
  applicationId: string;
  interruptedChatId?: never;
  headers?: never;
  ingress: WiseOwlIngress.console;
  ticketId?: never;
}

export interface WiseOwlGetChatByIdArgsZendesk
  extends WiseOwlGetChatByIdArgsBase {
  applicationId?: never;
  interruptedChatId?: never;
  headers: WiseOwlHeadersZendesk;
  ingress: WiseOwlIngress.zendesk;
  ticketId: string;
}

export interface WiseOwlGetChatByIdArgsSalesforce
  extends WiseOwlGetChatByIdArgsBase {
  applicationId?: never;
  interruptedChatId?: never;
  headers: WiseOwlHeadersSalesforce;
  ingress: WiseOwlIngress.salesforce;
  ticketId?: never;
}

export interface WiseOwlGetChatByIdArgsWiseOwlApp
  extends WiseOwlGetChatByIdArgsBase {
  applicationId?: never;
  interruptedChatId?: never;
  headers: WiseOwlHeadersWiseOwlApp;
  ingress: WiseOwlIngress.wiseOwlApp;
  ticketId?: never;
}

export interface WiseOwlGetChatsByUserResponseData {
  chats: WiseOwlChatType[];
}

export interface WiseOwlGetChatByIdResponseData {
  chat: WiseOwlChatType;
  blocks: WiseOwlBlock[];
}

/**
 * GET CHAT ABOUT TICKET ID
 */
export interface WiseOwlGetChatAboutTicketIdArgs extends RequestMetaData {
  aboutTicketId: number;
  headers: WiseOwlHeadersZendesk;
  ingress: WiseOwlIngress;
}

export interface WiseOwlGetChatAboutTicketIdResponseData {
  chats: WiseOwlChatType[];
  blocks: WiseOwlBlock[];
}

/**
 * TRANSFER TO LIVE CHAT
 */
export interface LiveChatProps extends LiveChatBlockType {
  element: string;
  appContainerId: string;
  handleGetChatSummary?: () => Promise<
    WiseOwlGetChatSummaryByIdResponseData | undefined
  >;
}

export interface LiveChatBlockType {
  liveChatId: string;
  token: string;
  identity: string;
  ticketId: number;
  chatActivitySyncMapId: string;
  chatId?: string;
  endedActions?: EndedActions;
  waitingActions?: WaitingActions;
  ingress?: WiseOwlIngress;
  wiseOwlToken?: string;
  user?: WiseOwlUser;
}

export type LiveChatBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.LiveChat,
  LiveChatBlockType
>;

export type WiseOwlTransferToLiveChatArgs =
  | WiseOwlTransferToLiveChatArgsOsc
  | WiseOwlTransferToLiveChatArgsConsole
  | WiseOwlTransferToLiveChatArgsSendGrid;

export interface WiseOwlTransferToLiveChatArgsSendGrid extends RequestMetaData {
  ingress: WiseOwlIngress.sendGrid;
  chatId: string;
  headers: WiseOwlHeadersSendgrid;
  body: WiseOwlTransferToLiveChatBodySendGrid;
}
export interface WiseOwlTransferToLiveChatArgsOsc extends RequestMetaData {
  ingress: WiseOwlIngress.osc;
  chatId: string;
  headers?: never;
  body: WiseOwlTransferToLiveChatBodyOsc;
}

export interface WiseOwlTransferToLiveChatArgsConsole extends RequestMetaData {
  ingress: WiseOwlIngress.console;
  chatId: string;
  headers?: never;
  body: WiseOwlTransferToLiveChatBodyConsole;
}

export interface WiseOwlTransferToLiveChatBaseBody {
  chatFriendlyName: string;
  customerFriendlyName: string;
  preEngagementData: string;
}

export interface WiseOwlTransferToLiveChatBodyConsole
  extends WiseOwlTransferToLiveChatBaseBody {
  applicationId?: string;
}

export interface WiseOwlTransferToLiveChatBodyOsc
  extends WiseOwlTransferToLiveChatBaseBody {
  applicationId?: never;
}
export interface WiseOwlTransferToLiveChatBodySendGrid
  extends WiseOwlTransferToLiveChatBaseBody {
  applicationId?: never;
}

export interface WiseOwlTransferToLiveChatResponseData {
  blocks: LiveChatBlock[];
}

/**
 * GET CHAT SUMMARY BY ID
 */
export interface WiseOwlGetUngatedChatSummaryBody {
  stargateUngatedUserId: string;
}

export type WiseOwlGetChatSummaryByIdArgs =
  | WiseOwlGetChatSummaryByIdArgsOsc
  | WiseOwlGetChatSummaryByIdArgsConsole
  | WiseOwlGetChatSummaryByIdArgsZendesk
  | WiseOwlGetChatSummaryByIdArgsWiseOwlApp
  | WiseOwlGetChatSummaryByIdArgsSalesforce
  | WiseOwlGetChatSummaryByIdArgsSendGrid;

export interface WiseOwlGetChatSummaryByIdArgsDefault extends RequestMetaData {
  chatId: string;
}

export interface WiseOwlGetChatSummaryByIdArgsSendGrid
  extends WiseOwlGetChatSummaryByIdArgsDefault {
  applicationId?: never;
  ingress: WiseOwlIngress.sendGrid;
  headers: WiseOwlHeadersSendgrid;
  ticketId?: never;
}

export interface WiseOwlGetChatSummaryByIdArgsOsc
  extends WiseOwlGetChatSummaryByIdArgsDefault {
  applicationId?: never;
  ingress: WiseOwlIngress.osc;
  headers?: never;
  ticketId?: never;
}

export interface WiseOwlGetChatSummaryByIdArgsConsole
  extends WiseOwlGetChatSummaryByIdArgsDefault {
  applicationId: string;
  ingress: WiseOwlIngress.console;
  headers?: never;
  ticketId?: never;
}

export interface WiseOwlGetChatSummaryByIdArgsZendesk
  extends WiseOwlGetChatSummaryByIdArgsDefault {
  applicationId?: never;
  ingress: WiseOwlIngress.zendesk;
  headers: WiseOwlHeadersZendesk;
  ticketId: string;
}

export interface WiseOwlGetChatSummaryByIdArgsSalesforce
  extends WiseOwlGetChatSummaryByIdArgsDefault {
  applicationId?: never;
  ingress: WiseOwlIngress.salesforce;
  headers: WiseOwlHeadersSalesforce;
  ticketId?: never;
}

export interface WiseOwlGetChatSummaryByIdArgsWiseOwlApp
  extends WiseOwlGetChatSummaryByIdArgsDefault {
  applicationId?: never;
  ingress: WiseOwlIngress.wiseOwlApp;
  headers: WiseOwlHeadersWiseOwlApp;
  ticketId?: never;
}

export interface WiseOwlGetUngatedChatSummaryByIdArgs extends RequestMetaData {
  chatId: string;
  body: WiseOwlGetUngatedChatSummaryBody;
  headers?: never;
}

export interface WiseOwlGetChatSummaryByIdResponseData {
  subject: string;
  description: string;
  wasTranslated: boolean;
}

/**
 * CREATE CHAT
 */
export type WiseOwlCreateChatArgs =
  | WiseOwlCreateArgsOsc
  | WiseOwlCreateUngatedArgsOsc
  | WiseOwlCreateArgsZendesk
  | WiseOwlCreateArgsSalesforce
  | WiseOwlCreateArgsWiseOwlApp
  | WiseOwlCreateArgsConsole
  | WiseOwlCreateArgsSendGrid;

export interface WiseOwlCreateArgsSendGrid extends RequestMetaData {
  ingress: WiseOwlIngress.sendGrid;
  body: WiseOwlCreateUpdateBodySendGrid;
  headers: WiseOwlHeadersSendgrid;
}

export interface WiseOwlCreateArgsOsc extends RequestMetaData {
  ingress: WiseOwlIngress.osc;
  body: WiseOwlCreateUpdateBodyOsc;
  headers?: never;
}

export interface WiseOwlCreateUngatedArgsOsc extends RequestMetaData {
  ingress: WiseOwlIngress.oscUngated;
  body: WiseOwlCreateUpdateBodyOscUngated;
  headers?: never;
}

export interface WiseOwlCreateArgsSalesforce extends RequestMetaData {
  ingress: WiseOwlIngress.salesforce;
  body: WiseOwlCreateUpdateBodySalesforce;
  headers: WiseOwlHeadersSalesforce;
}

export interface WiseOwlCreateArgsWiseOwlApp extends RequestMetaData {
  ingress: WiseOwlIngress.wiseOwlApp;
  body: WiseOwlCreateUpdateBodyWiseOwlApp;
  headers: WiseOwlHeadersWiseOwlApp;
}

export interface WiseOwlCreateArgsZendesk extends RequestMetaData {
  ingress: WiseOwlIngress.zendesk;
  body: WiseOwlCreateUpdateBodyZendesk;
  headers: WiseOwlHeadersZendesk;
}

export interface WiseOwlCreateArgsConsole extends RequestMetaData {
  ingress: WiseOwlIngress.console;
  body: WiseOwlCreateUpdateBodyConsole;
  headers?: never;
}

export interface WiseOwlCreateChatResponseData {
  accessToken: string;
  chatId: string;
  syncDocumentSid: string;
}

/**
 * CREATE FEEDBACK
 */
export type WiseOwlCreateFeedbackArgs =
  | WiseOwlCreateFeedbackArgsOsc
  | WiseOwlCreateUngatedFeedbackArgsOsc
  | WiseOwlCreateFeedbackArgsConsole
  | WiseOwlCreateFeedbackArgsZendesk
  | WiseOwlCreateFeedbackArgsWiseOwlApp
  | WiseOwlCreateFeedbackArgsSalesforce
  | WiseOwlCreateFeedbackArgsSendGrid;

export interface WiseOwlCreateFeedbackArgsSendGrid
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.sendGrid;
  body: WiseOwlCreateFeedbackBodySendGrid;
  headers: WiseOwlHeadersSendgrid;
}

export interface WiseOwlCreateFeedbackDefaultArgs extends RequestMetaData {
  chatId: string;
  messageId: string;
}

export interface WiseOwlCreateFeedbackArgsOsc
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.osc;
  body: WiseOwlCreateFeedbackBodyOsc;
  headers?: never;
}

export interface WiseOwlCreateUngatedFeedbackArgsOsc
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.oscUngated;
  body: WiseOwlCreateFeedbackBodyOscUngated;
  headers?: never;
}

export interface WiseOwlCreateFeedbackArgsConsole
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.console;
  body: WiseOwlCreateFeedbackBodyConsole;
  headers?: never;
}

export interface WiseOwlCreateFeedbackArgsZendesk
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.zendesk;
  body: WiseOwlCreateFeedbackBodyInternal;
  headers: WiseOwlHeadersZendesk;
}

export interface WiseOwlCreateFeedbackArgsSalesforce
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.salesforce;
  body: WiseOwlCreateFeedbackBodyInternal;
  headers: WiseOwlHeadersSalesforce;
}

export interface WiseOwlCreateFeedbackArgsWiseOwlApp
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.wiseOwlApp;
  body: WiseOwlCreateFeedbackBodyInternal;
  headers: WiseOwlHeadersWiseOwlApp;
}

/**
 * UPDATE CHAT
 */
export type WiseOwlUpdateChatArgs =
  | WiseOwlUpdateChatArgsOsc
  | WiseOwlUpdateUngatedArgsOsc
  | WiseOwlUpdateArgsSalesforce
  | WiseOwlUpdateArgsZendesk
  | WiseOwlUpdateArgsWiseOwlApp
  | WiseOwlUpdateArgsConsole
  | WiseOwlUpdateArgsSendGrid;

export interface WiseOwlUpdateArgsSendGrid extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.sendGrid;
  body: WiseOwlCreateUpdateBodySendGrid;
  headers: WiseOwlHeadersSendgrid;
}

export interface WiseOwlUpdateChatArgsBase extends RequestMetaData {
  chatId: string;
}

export interface WiseOwlUpdateChatArgsOsc extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.osc;
  body: WiseOwlCreateUpdateBodyOsc;
  headers?: never;
}

export interface WiseOwlUpdateUngatedArgsOsc extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.oscUngated;
  body: WiseOwlCreateUpdateBodyOscUngated;
  headers?: never;
}

export interface WiseOwlUpdateArgsSalesforce extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.salesforce;
  body: WiseOwlCreateUpdateBodySalesforce;
  headers: WiseOwlHeadersSalesforce;
}

export interface WiseOwlUpdateArgsWiseOwlApp extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.wiseOwlApp;
  body: WiseOwlCreateUpdateBodyWiseOwlApp;
  headers: WiseOwlHeadersWiseOwlApp;
}

export interface WiseOwlUpdateArgsZendesk extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.zendesk;
  body: WiseOwlCreateUpdateBodyZendesk;
  headers: WiseOwlHeadersZendesk;
}

export interface WiseOwlUpdateArgsConsole extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.console;
  body: WiseOwlCreateUpdateBodyConsole;
  headers?: never;
}

export interface WiseOwlUpdateChatResponseData {
  accessToken: string;
  syncDocumentSid: string;
}

/**
 * @TODO !IMPORTANT
 * Last update: 06.01.2024
 *
 * We eventually want to be able to import these types from
 * ops-services-platform. In the interim, we have duplicated them here. It is
 * crucial to ensure that this file is in sync with
 * https://github.com/twilio-internal/ops-services-platform/blob/main/libs/wise-owl/shared/blocks/src/lib/definitions/types.ts
 */

/**
 * BLOCKS
 */
export type WiseOwlBlock =
  | MessageBlock
  | CardGroupBlock
  | ToolCallBlock
  | LinkGroupBlock
  | LiveChatBlock
  | ButtonGroupBlock
  | ComboboxBlock
  | PromptSuggestionGroupBlock
  | SubmitTicketFormBlock
  | TableBlock
  | IntelligentDiscoveryHomeBlock
  | LoginCTABlock
  | ToolCallStatusLineBlock
  | TableBlock
  | DataGridBlock
  | SupportCtaBlock
  | FeedbackBlock;

export enum WiseOwlBlockType {
  Message = "message",
  ToolCall = "tool-call",
  CardGroup = "card-group",
  LinkGroup = "link-group",
  LiveChat = "live-chat",
  ButtonGroup = "button-group",
  PromptSuggestionGroup = "prompt-suggestion-group",
  SubmitTicketForm = "submit-ticket-form",
  ToolCallStatusLine = "tool-call-status-line",
  TableBlock = "table",
  IntelligentDiscoveryHome = "intelligent-discovery-home",
  LoginCTA = "login-cta",
  DataGrid = "data-grid",
  SupportCta = "support-cta",
  Feedback = "feedback",
  Combobox = "combobox",
}

export enum WiseOwlBlockDisplayType {
  Persistent = "persistent",
  Transient = "transient",
}

export interface WiseOwlBaseBlock<
  Type extends WiseOwlBlockType,
  Values extends object
> {
  type: Type;
  // Persistent blocks are always displayed. Transient blocks are only displayed
  // if they are the most recent message. This is applicable to something like a
  // button where we don't want to render it when it is no longer relevant. We
  // can add more display types as needed to support future use cases.
  display: WiseOwlBlockDisplayType;
  values: Values;
}

// column -> value
export type DataGridRowValues = Record<string, unknown>;
// column -> number of rows to span
export type DataGridRowSpans = Record<string, number>;
// column -> WiseOwlAction
export type DataGridRowActions = Record<string, WiseOwlAction>;

export interface DataGridRow {
  values: DataGridRowValues;
  spans?: DataGridRowSpans;
  actions?: DataGridRowActions;
}

export interface DataGridColumn {
  name: string;
  displayName: string;
}

export type DataGridBlock = WiseOwlBaseBlock<
  typeof WiseOwlBlockType.DataGrid,
  {
    description: string;
    rows: DataGridRow[];
    columns: DataGridColumn[];
  }
>;

export const MessageBlockRole = {
  Assistant: "assistant",
  User: "human",
} as const;
export type MessageBlockRoleType = ValueOf<typeof MessageBlockRole>;

export interface BaseMessageValues {
  id: string;
  role: MessageBlockRoleType;
  message: string;
  retryId?: string;
  success?: boolean;
  createChatOnRetry?: boolean;
  completionStatus?: MessageCompletionStatus;
}

export interface CompleteMessageValues extends BaseMessageValues {
  isComplete: true;
  delta: null;
}

export interface IncompleteMessageValues extends BaseMessageValues {
  isComplete: false;
  delta: string;
}

export type MessageBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.Message,
  CompleteMessageValues | IncompleteMessageValues
>;

export type ToolCallBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.ToolCall,
  {
    name: string;
    input: string;
    output: string;
    formattedOutput: string;
  }
>;

export type LoginCTABlock = WiseOwlBaseBlock<
  WiseOwlBlockType.LoginCTA,
  {
    buttons: ButtonValues[];
  }
>;

export type ModalAction = WiseOwlBaseAction<
  typeof WiseOwlActionType.Modal,
  {
    modalTitle: string;
    modalBody: string;
  }
>;

export type LiveChatAction = WiseOwlBaseAction<
  typeof WiseOwlActionType.LiveChat,
  {
    productCategory: string;
    summary: string;
  }
>;

export type CallbackRequestAction = WiseOwlBaseAction<
  typeof WiseOwlActionType.CallbackRequest,
  {
    phoneNumber: string;
  }
>;

export interface LinkValues {
  preview: string;
  title: string;
  url: string;
}

export type LinkGroupBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.LinkGroup,
  { links: LinkValues[] }
>;

export enum WiseOwlEventName {
  SubmitTicket = "submit-ticket",
}

export type EventAction = WiseOwlBaseAction<
  WiseOwlActionType.Event,
  {
    name: WiseOwlEventName;
    metadata: Record<string, unknown>;
  }
>;

export type RedirectAction = WiseOwlBaseAction<
  WiseOwlActionType.Redirect,
  {
    redirectUrl: string;
  }
>;

export type UserSayAction = WiseOwlBaseAction<
  WiseOwlActionType.UserSay,
  {
    content: string;
  }
>;

// This enum should be in sync with OSP libs/wise-owl/shared/blocks/src/lib/definitions/types.ts
export enum WiseOwlActionType {
  Event = "event",
  Redirect = "redirect",
  DeepLink = "deep-link",
  Modal = "modal",
  LiveChat = "live-chat",
  UserSay = "user-say",
  CallbackRequest = "callback-request",
}

export interface WiseOwlBaseAction<
  Type extends WiseOwlActionType,
  Values extends object
> {
  type: Type;
  values: Values;
}

export const SupportCtaChannel = {
  LiveChat: "live-chat",
  HelpCenter: "help-center",
  Ticket: "ticket",
} as const;
export type SupportCtaChannelType = ValueOf<typeof SupportCtaChannel>;

/**
 * This action is similar to the {@link RedirectAction}
 * with the main difference being that the UI will handle
 * generation of the deep link URL based on the {@link DeepLinkPathType}
 * provided, while the {@link RedirectAction} requires the URL to be
 * generated by the backend.
 */
export type DeepLinkAction = WiseOwlBaseAction<
  typeof WiseOwlActionType.DeepLink,
  {
    /** The path to redirect to. */
    deepLinkPath: DeepLinkPathType;
    /**
     * Persist the chat history when the
     * deep link is opened.
     */
    persistChat?: boolean;
  }
>;
export const DeepLinkPath = {
  LOGIN: "login",
  SIGN_UP: "try-twilio",
} as const;
export type DeepLinkPathType = ValueOf<typeof DeepLinkPath>;

export type WiseOwlAction =
  | EventAction
  | RedirectAction
  | DeepLinkAction
  | ModalAction
  | LiveChatAction
  | CallbackRequestAction
  | UserSayAction;

export interface CardButtonValues {
  text: string;
  type: string;
  action: WiseOwlAction;
}

export interface CardLabels {
  text: string;
}

export interface CardDescriptions {
  text: string;
}

export enum CardType {
  PhoneSupport = "phone",
  SubmitTicket = "ticket",
  LiveChat = "chat",
}

export interface CardValues {
  type: CardType;
  label: CardLabels;
  description: CardDescriptions;
  button: CardButtonValues;
}

export type CardGroupBlock = WiseOwlBaseBlock<
  typeof WiseOwlBlockType.CardGroup,
  {
    cards: CardValues[];
  }
>;

export interface Item {
  displayName: string;
  id: string;
  name: string;
}

export type ComboboxBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.Combobox,
  {
    items: Item[];
    label: string;
  }
>;

export interface ButtonValues {
  text: string;
  action: WiseOwlAction;
}

export type ButtonGroupBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.ButtonGroup,
  {
    buttons: ButtonValues[];
  }
>;

export type SubmitTicketFormBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.SubmitTicketForm,
  {
    ticketDescription: string;
    ticketBody: string;
    optionalQuestions: string[];
  }
>;

export interface PromptSuggestionCategory {
  name: string;
  suggestions: string[];
}

export type PromptSuggestionGroupBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.PromptSuggestionGroup,
  {
    categories: PromptSuggestionCategory[];
  }
>;

export type ToolCallStatusLineBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.ToolCallStatusLine,
  {
    content: string;
  }
>;

export type IntelligentDiscoveryHomeBlock = WiseOwlBaseBlock<
  typeof WiseOwlBlockType.IntelligentDiscoveryHome,
  {
    lastNDays: number;
    failedMessages: number;
    undeliveredMessages: number;
    totalMessages: number;
  }
>;

export type TableBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.TableBlock,
  {
    records: Record<string, unknown>[];
  }
>;

export type SupportCtaBlock = WiseOwlBaseBlock<
  typeof WiseOwlBlockType.SupportCta,
  {
    channel: SupportCtaChannelType;
  }
>;

/**
 * CHATS
 */
/**
 * * Note: These types are generated in the API by Prisma and do not exists
 * * as an explicit types file in OSP, which means the below are the best source
 * * of truth for our client-side applications. The properties are also not
 * * alphabetized to more closely match the Prisma schema. Props that are not used
 * * in OWP are also not include (e.g. slackThreadTimestamp, slackConversationId)
 */
export interface WiseOwlChatType {
  id: string;
  blocks: WiseOwlBlock[];
  createdAt: string;
  updatedAt: string;
  userId: string;
  title: string;
  ingress: WiseOwlApiIngress;
  slackConversationId: string | null;
  slackThreadTimestamp: string | null;
  messages: WiseOwlMessage[];
  aboutTicketId: number;
}

export type WiseOwlChatHistoryItem = Omit<WiseOwlChatType, "messages">;

export interface WiseOwlMessage {
  id: string;
  createdAt: string;
  chatId: string;
  updatedAt: string;
  messageRole: MessageRole;
  content: string;
  userId: string | null;
  slackMessageTimestamp: string | null;
  isComplete: boolean | null;
  toolCalls: WiseOwlToolCall[];
}

// * Note: Tool calls should only be shown on internal applications
export interface WiseOwlToolCall {
  id: string;
  createdAt: string;
  updatedAt: string;
  messageId: string;
  name: string;
  input: string;
  output: string;
  formattedOutput: string;
}

export enum WiseOwlApiIngress {
  OneAdmin = "ONE_ADMIN",
  OneSupportCenter = "ONE_SUPPORT_CENTER",
  Salesforce = "SALESFORCE",
  Zendesk = "ZENDESK",
}

export enum MessageRole {
  User = "USER",
  Assistant = "ASSISTANT",
  System = "SYSTEM",
}

export const FeedbackChoice = {
  ThumbsUp: "thumbs-up",
  ThumbsDown: "thumbs-down",
} as const;
export type FeedbackChoiceType = ValueOf<typeof FeedbackChoice>;

export type FeedbackBlock = WiseOwlBaseBlock<
  typeof WiseOwlBlockType.Feedback,
  {
    messageId: string;
    selected: FeedbackChoiceType | null;
    supportCtaChannels: SupportCtaChannelType[];
  }
>;
